import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Map, Wrench, GraduationCap } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSEO } from "@/lib/seo";
import { GoAndTrackLink, linkifyGoAndTrack } from "@/lib/goAndTrack";

export default function About() {
  useSEO({
    title: "About the Programme | Disposable Smart Labels",
    description:
      "How we approach disposable smart labels for shipment visibility — pilot setup, integration, and operational delivery via GoAndTrack.",
    canonical: "/about",
  });

  return (
    <div data-testid="page-about">
      <PageHero
        eyebrow="About"
        title="Disposable smart labels, deployed in real lanes."
        subtitle="A focused programme dedicated to a single category — disposable smart labels for shipment visibility — with operational delivery powered by GoAndTrack."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="about-cta">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">What this is</p>
            <h2 className="h2">A focused category programme.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5">
            <p className="body-text">
              This site exists for one reason: to help logistics, supply chain,
              innovation, and operations teams evaluate whether{" "}
              <strong className="text-ink">disposable smart labels</strong>{" "}
              are the right shipment-visibility approach for a specific lane,
              programme, or pilot.
            </p>
            <p className="body-text">
              We are deliberately narrow. We do not sell reusable trackers,
              fleet hardware, or general supply-chain software. We work with a
              single category — single-use smart labels — and deploy them
              against the shipment profiles where they fit best: high-value
              cargo, temporary tracking, partner-heavy lanes, returns flows,
              and structured pilots.
            </p>
            <p className="body-text">
              Operational visibility is delivered through{" "}
              <GoAndTrackLink className="font-medium" />, the platform
              layer that turns disposable label data into live tracking,
              alerts, and exception workflows for your operations team and
              partners.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">How we work</p>
            <h2 className="h2">Specialist support, end-to-end.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {[
              {
                icon: Map,
                k: "Pilot setup",
                v: "We scope, deploy, and review structured pilots in real shipment lanes — not slide decks.",
              },
              {
                icon: Wrench,
                k: "Integration",
                v: "Hands-on technical engagement to wire GoAndTrack into existing TMS, WMS, and ERP environments.",
              },
              {
                icon: GraduationCap,
                k: "Operational training",
                v: "Practical training for warehouse, dispatch, and partner teams — built for the floor.",
              },
              {
                icon: ShieldCheck,
                k: "Continuous review",
                v: "Quarterly reviews against shipment-visibility KPIs, with structured next-step recommendations.",
              },
            ].map((b) => (
              <div key={b.k} className="bg-white p-7 lg:p-8">
                <b.icon className="h-5 w-5 text-brand" />
                <h3 className="h3 mt-4">{b.k}</h3>
                <p className="body-text mt-2 text-sm">{linkifyGoAndTrack(b.v)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">Platform layer</p>
            <h2 className="h2"><GoAndTrackLink /> — the operational backbone.</h2>
            <p className="body-text mt-5">
              <GoAndTrackLink /> is the platform layer behind every deployment. It
              turns disposable label data into the live tracking, alerting, and
              exception workflows that operations teams actually use — without
              forcing your business to build or maintain its own
              shipment-visibility software.
            </p>
            <p className="body-text mt-4">
              For most teams, <GoAndTrackLink /> is the practical answer to the
              question &quot;what do I do with all this shipment data, and who needs
              to see it?&quot;.
            </p>
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line">
            {[
              { k: "Live tracking", v: "Location, dwell, and journey timelines in one operational view." },
              { k: "Exception alerts", v: "Configurable thresholds and escalation rules for ops, partners, and customers." },
              { k: "Permissioned access", v: "Role-based views for internal teams, carriers, partners, and end customers." },
              { k: "API integration", v: "Webhooks and APIs for TMS, WMS, ERP, and downstream BI integration." },
            ].map((b) => (
              <div key={b.k} className="bg-white p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                  {b.k}
                </p>
                <p className="mt-2 text-sm text-ink-muted">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedLinks
        title="Start here"
        links={[
          { to: "/disposable-smart-labels", label: "Disposable Smart Labels — category overview" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers — full comparison" },
          { to: "/smart-labels-for-pilot-projects", label: "Smart Labels for Pilot Projects — how pilots work" },
          { to: "/faqs", label: "FAQs — common questions from operations teams" },
        ]}
      />

      <CTAStrip
        title="Talk to a specialist."
        body="Pilot scoping, integration, and operational training — delivered by people who know the lanes."
      />
    </div>
  );
}
